/**
 * The shared wiring every op's engine adapter repeats: build the overlay +
 * structure facts, and the uniform `releaseComputed` / `createNewStructure` /
 * preview show-hide over the borrowed `ContourTexture` handle. Each
 * `useXxxEngineDeps` keeps its own `useCallback` (Rules of Hooks) and calls
 * this PLAIN function inside it, passing only the op-specific `apply*` closure
 * and the engine undo-history label. The `handle as ContourTexture` casts are
 * the deliberate opacity seam (the port carries `handle: unknown`).
 */
import type { Environment } from '~/engine/data/Environment';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import type { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import type {
    GeometryWrite,
    AppliedGeometry,
} from '~/hooks/structure-set/applyComputedGeometry';
import { createPreviewOverlayPort } from './previewOverlay';
import { projectStructureFacts } from './engineStructureFacts';
import type { ComputedGeometry, OpDeps, BaseGeometryPort } from './pipeline';

/** An op's distinct `apply*` method(s) — the only part of `geometry` that
 *  varies between ops; the rest of the port is wired uniformly here. */
type OpGeometryMethods = Record<
    string,
    (...args: never[]) => Promise<ComputedGeometry>
>;

/** Resolve the source ROI's live texture or fail fast (no contour data). */
export function requireSourceTexture(
    env: Environment | null,
    roiNumber: number
): ContourTexture {
    const texture = env?.getRoi(roiNumber)?.texture;
    if (!texture) {
        throw new Error('source structure has no contour data');
    }
    return texture;
}

/**
 * Wrap an engine result texture as the port's opaque computed geometry.
 * The volume is a lazy getter (the texture caches it), so a result whose
 * volume is never read — e.g. an intermediate boolean combine — skips the
 * full-grid GPU readback entirely.
 */
export function toComputedGeometry(result: ContourTexture): ComputedGeometry {
    return {
        handle: result,
        get resultVolumeCm3(): number {
            return result.volumeCm3;
        },
    };
}

export function makeEngineDeps<G extends OpGeometryMethods>(
    env: Environment | null,
    structureSet: StructureSetReadModel | undefined,
    applyGeometry: (
        name: string,
        geometry: GeometryWrite
    ) => Promise<AppliedGeometry>,
    config: {
        readonly label: string;
        readonly geometry: (env: Environment | null) => G;
    }
): OpDeps<ComputedGeometry> & { readonly geometry: BaseGeometryPort & G } {
    const overlay = createPreviewOverlayPort(env);
    const structures = projectStructureFacts(structureSet, env);

    return {
        structures,
        geometry: {
            ...config.geometry(env),
            releaseComputed(handle: ContourTexture | undefined): void {
                handle?.delete();
            },
        },
        commit: {
            async createNewStructure(name: string, computed: ComputedGeometry) {
                return applyGeometry(name, {
                    texture: computed.handle as ContourTexture,
                    label: config.label,
                });
            },
            // Read live: `env` is the mutable singleton, so this reflects the
            // active structure set at Apply time, not at plan time.
            currentContextId: () => env?.structureSet?.sopInstanceUID,
        },
        preview: {
            show: (computed) => overlay.show(computed.handle as ContourTexture),
            hide: () => overlay.hide(),
        },
    };
}
